import React, { useEffect, useState } from "react";
import { ChevronRight, Close, Watch } from "@material-symbols-svg/react";

import BottomSheet from "../../BottomSheet";
import LoaderFullScreen from "src/components/common/Loader/LoaderFullScreen";

import { Device, DeviceGroup } from "src/types/device.types";
import {
  DEVICE_GROUPS_ICONS,
  DEVICE_ICONS,
  DEVICE_IMAGES,
} from "src/constants/device";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getAvailableDevicesRequest } from "src/redux/slices/deviceSlice";

interface AvailableDeviceBottomSheetProps {
  show: boolean;
  setShow: (show: boolean) => void;
  startConnectionFlow: (deviceId: string) => void;
}

const AvailableDeviceBottomSheet: React.FC<AvailableDeviceBottomSheetProps> = ({
  show,
  setShow,
  startConnectionFlow,
}) => {
  const dispatch = useAppDispatch();
  const { availableDevices } = useAppSelector((state) => state.device);
  const [selectedGroup, setSelectedGroup] = useState<DeviceGroup | null>(null);

  useEffect(() => {
    if (show) {
      getAvailableDevices();
    }
  }, [show]);

  const getAvailableDevices = () => {
    new Promise((resolve, reject) => {
      dispatch(getAvailableDevicesRequest({ resolve, reject }));
    });
  };

  const handleClose = () => {
    setSelectedGroup(null);
    setShow(false);
  };

  const handleSelectGroup = (group: DeviceGroup) => setSelectedGroup(group);

  const handleBack = () => setSelectedGroup(null);

  const renderDeviceGroups = () => {
    return (
      <>
        <div className="space-y-2">
          {availableDevices?.data?.map((group: DeviceGroup) => (
            <button
              key={group.id}
              onClick={() => handleSelectGroup(group)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--surface-secondary)] border border-gray-100 hover:bg-gray-50 transition-colors text-left"
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  DEVICE_GROUPS_ICONS[
                    group?.id as keyof typeof DEVICE_GROUPS_ICONS
                  ].iconBg
                } text-[var(--app-primary)]`}
              >
                {
                  DEVICE_GROUPS_ICONS[
                    group?.id as keyof typeof DEVICE_GROUPS_ICONS
                  ].icon
                }
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium">{group?.title}</h3>
                <p className="text-sm text-[var(--text-secondary)]">
                  {group?.description}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-[var(--text-muted)] flex-shrink-0" />
            </button>
          ))}
        </div>
      </>
    );
  };

  const renderHeader = (headerType: string) => {
    let title: string = "";
    let description: string = "";
    let icon: React.ReactNode = (
      <Watch className="w-6 h-6 text-[var(--app-primary)]" />
    );

    if (headerType === "available") {
      title = "Pair a Device";
      description = "Choose a product group";
    } else if (selectedGroup) {
      title = selectedGroup.title;
      description = selectedGroup.description;
      icon =
        DEVICE_GROUPS_ICONS[
          selectedGroup.id as keyof typeof DEVICE_GROUPS_ICONS
        ].icon;
    }
    return (
      <div className="flex items-center justify-between p-4 w-full">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--icon-bg-primary)]">
            {icon}
          </div>
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              {description}
            </p>
          </div>
        </div>
        <button
          className="p-2 rounded-full bg-gray-100 border border-gray-200"
          onClick={selectedGroup ? handleBack : handleClose}
        >
          <Close className="w-4 h-4 text-[var(--text-secondary)]" />
        </button>
      </div>
    );
  };

  const renderDevices = () => {
    return (
      <>
        <div className="space-y-2">
          {selectedGroup?.devices.map((device: Device) => {
            let deviceIcon = null;
            let deviceImage: any = DEVICE_IMAGES[
              device.id as keyof typeof DEVICE_IMAGES
            ]
              ? DEVICE_IMAGES[device.id as keyof typeof DEVICE_IMAGES].image
              : null;

            if (!deviceImage) {
              deviceIcon = DEVICE_ICONS[device.id as keyof typeof DEVICE_ICONS]
                ? DEVICE_ICONS[device.id as keyof typeof DEVICE_ICONS].icon
                : null;
            }
            return (
              <button
                key={device.id}
                onClick={() => startConnectionFlow(device.id)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--surface-secondary)] border border-gray-100 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 text-[var(--app-primary)]">
                  {deviceImage ? (
                    <img
                      src={deviceImage}
                      alt={device.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : deviceIcon ? (
                    deviceIcon
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium">{device.name}</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {device.description}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-[var(--text-muted)] flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </>
    );
  };

  if (availableDevices.isLoading) {
    return <LoaderFullScreen show={availableDevices.isLoading} />;
  }

  return (
    <BottomSheet
      show={show}
      handleClose={handleClose}
      height={50}
      header={renderHeader(selectedGroup === null ? "available" : "selected")}
    >
      <div className="px-6 py-4">
        {selectedGroup === null ? renderDeviceGroups() : renderDevices()}
      </div>
    </BottomSheet>
  );
};

export default AvailableDeviceBottomSheet;
